/**
 * FILE-OVERVIEW:
 * - 役割: スケジュール画面のフィルタUI・ユーザー情報・ログアウトを管理。
 * - 主な連携: hooks/useCalendarState.js, utils/colors.js
 * - 本番運用上の注意: スクロール領域と操作ボタン位置はモバイル表示を含めて確認する。
 */

import React from 'react';
import { 
  Drawer, 
  Box, 
  IconButton, 
  Button, 
  FormControlLabel, 
  Typography 
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import CustomSwitch from '../CustomSwitch';
import { categoryColors, areaColors, scheduleFilterButtonColors } from '../../utils/colors';
import { fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth'; // 追加: fetchAuthSession を使用
import { resolveUserGroupLabel } from '../../utils/userGroupLabel';
import type { ButtonProps } from '@mui/material';
import type { CalendarStateApi } from '../../utils/eventHelpers';
import type { AuthUser } from '../../hooks/useAuth';

interface FilterSidebarProps {
  isMobile: boolean;
  calendarState: CalendarStateApi;
  onSignOut?: () => void;
  user?: AuthUser | null;
  isDesk?: boolean;
}

const FilterSidebar = ({ isMobile, calendarState, onSignOut, user, isDesk }: FilterSidebarProps) => {
  const desktopSidebarHeight = 'calc(100dvh - 96px)';
  const [cognitoName, setCognitoName] = React.useState('');
  const visibleCategories = React.useMemo(() => Object.keys(categoryColors), []);
  const areaFilterButtonColors = React.useMemo<Record<string, string>>(() => ({
    本社: '#f4511e',
    青森: '#3f51b5',
    総支局: '#0b8043',
  }), []);
  // 初期値は props の isDesk を考慮しつつ、既定は「ユーザー」
  const [groupLabel, setGroupLabel] = React.useState(isDesk ? 'デスク' : 'ユーザー');

  React.useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        // ユーザー属性（表示名）
        const attrs = await fetchUserAttributes().catch(() => null);
        if (mounted && attrs) {
          const display =
            attrs?.name ||
            ([attrs?.family_name, attrs?.given_name].filter(Boolean).join(' ') || '') ||
            attrs?.preferred_username ||
            '';
          setCognitoName(display);
        }
      } catch (e) {
        console.warn('ユーザー属性取得失敗', e);
      }

      // グループ（cognito:groups）を ID トークンから取得
      try {
        const session = await fetchAuthSession();
        // Amplify v6 の形: session.tokens.idToken.payload['cognito:groups']
        // v5形式(session.idToken)のフォールバックは現在の型に無いためキャストして読む
        const legacySession = session as unknown as {
          idToken?: { payload?: Record<string, unknown> };
        };
        const groups =
          session?.tokens?.idToken?.payload?.['cognito:groups'] ??
          legacySession?.idToken?.payload?.['cognito:groups'] ??
          [];
        if (mounted) {
          setGroupLabel(resolveUserGroupLabel(groups));
        }
      } catch (e) {
        // 取得できない場合は props の isDesk をフォールバック
        if (mounted) setGroupLabel(isDesk ? 'デスク' : 'ユーザー');
        console.warn('グループ情報取得失敗', e);
      }
    })();

    return () => { mounted = false; };
  }, [isDesk]);

  return (
    <Drawer
      anchor="right"
      open={!isMobile || calendarState.isDrawerOpen}
      onClose={calendarState.toggleDrawer(false)}
      variant={!isMobile ? 'persistent' : 'temporary'}
      sx={{
        '& .MuiDrawer-paper': {
          width: !isMobile ? 300 : '100vw',
          bgcolor: '#f5f5f5',
          boxShadow: 3,
          p: 2,
          pb: 2,
          height: !isMobile ? desktopSidebarHeight : '100dvh',
          maxHeight: !isMobile ? desktopSidebarHeight : '100dvh',
          overflow: 'hidden',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          position: !isMobile ? 'relative' : 'fixed',
          maxWidth: !isMobile ? 300 : '100vw',
        },
      }}
    >
      {isMobile && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mt: 0,
            mb: 2,
          }}
        >
          <IconButton
            onClick={calendarState.toggleDrawer(false)}
            aria-label="close"
            sx={{ right: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          pr: 0.5,
          pb: 2,
        }}
      >
      {/* ユーザーステータス（グループ表記） */}
      <Box sx={{ mt: 1, mb: 2, textAlign: 'center', width: '100%' }}>
        <Box
          sx={{
            display: 'inline-block',
            px: 4,
            py: 1,
            bgcolor: '#e0e0e0',
            borderRadius: 2,
            border: '1px solid #bdbdbd',
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px',
          }}
        >
          <Typography variant="body2" component="div">
            <Box sx={{mt: 1, mb: 1, textAlign: 'left'}}>
              ユーザー：{cognitoName ? `${cognitoName}` : ''}
            </Box>
            <Box sx={{mt: 1, mb: 1, textAlign: 'left'}}>
              グループ：{groupLabel}
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  if (window.confirm('ログアウトしますか？')) {
                    onSignOut?.();
                  }
                }}
                size="small"
                sx={{
                  display: 'inline-block',
                  bgcolor: '#d50000',
                  color: '#fff',
                  fontWeight: 'bold',
                  letterSpacing: '0.05em',
                  '&:hover': { bgcolor: '#b71c1c' },
                  mt: '30px',
                  mb: '15px',
                  px: 3,
                  py: 0.5,
                }}
              >
                ログアウト
              </Button>
            </Box>
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          width: 'fit-content',
          mx: 'auto',
          mt: isMobile ? 1 : 3,
          mb: isMobile ? 1 : 3,
      }}
      >
        <FormControlLabel
          control={
            <CustomSwitch
              checked={calendarState.autoUpdate}
              onChange={(e) => calendarState.setAutoUpdate(e.target.checked)}
            />
          }
          label="自動更新"
          sx={{
            ml: 0,
            justifyContent: 'flex-start',
            gap: 1.2,
            '& .MuiFormControlLabel-label': {
              fontSize: '1.0rem',
              fontWeight: 'bold',
              color: calendarState.autoUpdate ? '#000000' : '#a9a9a9',
              transition: 'color 0.5s ease-in-out',
            },
          }}
        />
        <FormControlLabel
          control={
            <CustomSwitch
              checked={calendarState.showOnlyMine}
              onChange={(e) => calendarState.setShowOnlyMine(e.target.checked)}
            />
          }
          label="自分の予定のみ"
          sx={{
            mt: 1,
            ml: 0,
            justifyContent: 'flex-start',
            gap: 1.2,
            '& .MuiFormControlLabel-label': {
              fontSize: '1.0rem',
              fontWeight: 'bold',
              color: calendarState.showOnlyMine ? '#000000' : '#a9a9a9',
              transition: 'color 0.5s ease-in-out',
            },
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          mt: 3,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
          {Object.keys(areaColors).map((area) => (
            <Button
              key={area}
              variant={calendarState.selectedAreas.includes(area) ? 'contained' : 'outlined'}
              // 'default' は MUI v5 で廃止された色で v6 の型にも無い。
              // 実際の配色は下の sx で指定しており効いていないが、
              // 挙動を変えないため値は残して型だけ通している
              color={(calendarState.selectedAreas.includes(area) ? 'primary' : 'default') as unknown as ButtonProps['color']}
              onClick={() => {
                if (calendarState.selectedAreas.includes(area)) {
                  calendarState.setSelectedAreas(calendarState.selectedAreas.filter((a) => a !== area));
                } else {
                  calendarState.setSelectedAreas([...calendarState.selectedAreas, area]);
                }
              }}
              sx={{
                ...(function () {
                  const areaColor = areaFilterButtonColors[area] || scheduleFilterButtonColors.background;
                  return {
                    textTransform: 'none',
                    fontWeight: 'bold',
                    backgroundColor: calendarState.selectedAreas.includes(area)
                      ? areaColor
                      : scheduleFilterButtonColors.selectedBackground,
                    color: calendarState.selectedAreas.includes(area)
                      ? scheduleFilterButtonColors.text
                      : scheduleFilterButtonColors.selectedText,
                    borderColor: calendarState.selectedAreas.includes(area)
                      ? areaColor
                      : scheduleFilterButtonColors.border,
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    transition: 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease',
                    '&:hover': {
                      backgroundColor: areaColor,
                      borderColor: areaColor,
                    },
                  };
                })(),
              }}
            >
              {area}
            </Button>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
          {visibleCategories.map((category) => (
            <Button
              key={category}
              variant={calendarState.selectedCategories.includes(category) ? 'contained' : 'outlined'}
              // 'default' は MUI v5 で廃止された色で v6 の型にも無い。
              // 実際の配色は下の sx で指定しており効いていないが、
              // 挙動を変えないため値は残して型だけ通している
              color={(calendarState.selectedCategories.includes(category) ? 'primary' : 'default') as unknown as ButtonProps['color']}
              onClick={() => {
                if (calendarState.selectedCategories.includes(category)) {
                  calendarState.setSelectedCategories(calendarState.selectedCategories.filter((c) => c !== category));
                } else {
                  calendarState.setSelectedCategories([...calendarState.selectedCategories, category]);
                }
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 'bold',
                backgroundColor: calendarState.selectedCategories.includes(category)
                  ? scheduleFilterButtonColors.background
                  : scheduleFilterButtonColors.selectedBackground,
                color: calendarState.selectedCategories.includes(category)
                  ? scheduleFilterButtonColors.text
                  : scheduleFilterButtonColors.selectedText,
                borderColor: scheduleFilterButtonColors.border,
                borderWidth: '1px',
                borderStyle: 'solid',
                transition: 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease',
                '&:hover': {
                  backgroundColor: scheduleFilterButtonColors.hoverBackground,
                  borderColor: scheduleFilterButtonColors.hoverBorder,
                },
              }}
            >
              {category}
            </Button>
          ))}
        </Box>

        <Box
          sx={{ 
            display: 'flex',
            gap: 1,
            mt: 3,
            mb: 3,
            justifyContent: 'center',
            width: '100%'
          }
        }>
          <Button
            variant="outlined"
            onClick={() => calendarState.setSelectedCategories(visibleCategories)}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              backgroundColor: scheduleFilterButtonColors.background,
              color: scheduleFilterButtonColors.text,
              borderColor: scheduleFilterButtonColors.border,
              transition: 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease',
              '&:hover': {
                backgroundColor: scheduleFilterButtonColors.hoverBackground,
                color: scheduleFilterButtonColors.text,
                borderColor: scheduleFilterButtonColors.hoverBorder,
              },
            }}
          >
            全て選択
          </Button>
          <Button
            variant="outlined"
            onClick={() => calendarState.setSelectedCategories([])}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              backgroundColor: scheduleFilterButtonColors.background,
              color: scheduleFilterButtonColors.text,
              borderColor: scheduleFilterButtonColors.border,
              transition: 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease',
              '&:hover': {
                backgroundColor: scheduleFilterButtonColors.hoverBackground,
                color: scheduleFilterButtonColors.text,
                borderColor: scheduleFilterButtonColors.hoverBorder,
              },
            }}
          >
            全て解除
          </Button>
        </Box>

        <Box sx={{ mt: '45px', textAlign: 'center', width: '100%' }}>
          <Button
            component="a"
            href="http://192.168.63.133"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            size="small"
            sx={{
              width: '100%',
              bgcolor: '#1976d2',
              color: '#fff',
              border: '1px solid #fff',
              fontWeight: 'bold',
              letterSpacing: '0.05em',
              '&:hover': { bgcolor: '#1565c0' },
              mb: 0,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              py: 1,
            }}
          >
            アーカイブ
          </Button>

          <Button
            component="a"
            href="https://arc.daily-tohoku.jp/"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            size="small"
            sx={{
              width: '100%',
              bgcolor: '#1976d2',
              color: '#fff',
              border: '1px solid #fff',
              fontWeight: 'bold',
              letterSpacing: '0.05em',
              '&:hover': { bgcolor: '#1565c0' },
              mt: 0,
              mb: 2,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              py: 1,
            }}
          >
            紙面DB
          </Button>
        </Box>
      </Box>

      </Box>
    </Drawer>
  );
};

export default FilterSidebar;